import React from 'react';
import { CheckCircle, ChevronLeft, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

interface InfoSection {
  heading: string;
  body: string;
  items?: string[];
}

interface InfoPageProps {
  title: string;
  subtitle?: string;
  sections: InfoSection[];
}

const InfoPage: React.FC<InfoPageProps> = ({ title, subtitle, sections }) => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div className="bg-gradient-to-br from-fintech-primary/85 via-blue-800/78 to-slate-900/72 py-16 border-y border-white/20 backdrop-blur-[2px]">
        <div className="section-container text-center">
          <div className="public-readable-panel public-readable-panel--dark">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-blue-100 text-xs font-semibold uppercase tracking-wider mb-5">
            <FileText size={12} />
            Legal
          </div>
          <h1 className="text-4xl font-bold text-white mb-4 tracking-tight">{title}</h1>
          {subtitle && (
            <p className="text-blue-200 text-lg max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
          )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="section-container py-14">
        <div className="max-w-3xl mx-auto">
          {/* Table of contents */}
          {sections.length > 2 && (
            <div className="card p-5 mb-8">
              <h2 className="text-sm font-semibold text-fintech-text mb-3 flex items-center gap-2">
                <FileText size={14} className="text-fintech-secondary" />
                Table of Contents
              </h2>
              <ol className="flex flex-col gap-1.5">
                {sections.map((section, index) => (
                  <li key={section.heading}>
                    <a
                      href={`#section-${index}`}
                      className="flex items-center gap-2 text-sm text-fintech-secondary hover:text-fintech-primary no-underline hover:underline"
                    >
                      <span className="w-5 h-5 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-xs font-bold text-fintech-secondary flex-shrink-0">
                        {index + 1}
                      </span>
                      {section.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="flex flex-col gap-5">
            {sections.map((section, index) => (
              <div key={section.heading} id={`section-${index}`} className="card p-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 text-fintech-secondary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-fintech-text mb-3">{section.heading}</h2>
                    <p className="text-fintech-textMuted leading-relaxed mb-4 text-sm">{section.body}</p>
                    {section.items && section.items.length > 0 && (
                      <ul className="flex flex-col gap-2.5 pl-1">
                        {section.items.map((item) => (
                          <li key={item} className="flex items-start gap-2.5 text-sm text-fintech-textMuted">
                            <CheckCircle size={15} className="text-fintech-accent flex-shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Back link */}
          <div className="mt-8 flex items-center justify-between card p-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-fintech-secondary hover:text-fintech-primary font-medium no-underline"
            >
              <ChevronLeft size={14} />
              Back to Home
            </Link>
            <p className="text-xs text-fintech-textMuted">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoPage;
